import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Button as MatButton,
  TextField,
  TablePagination,
  TableSortLabel,
  Autocomplete,
} from "@mui/material";
import ViewIcons from "./../../../../assets/images/icons/view.svg";
import { useEffect, useState } from "react";
import Search from "../../../../components/Search/Search";
import { SCHEDULE_STATUS } from "../../../../utils/static-masters";
import API from "../../../../api/axios";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import DataNotFound from "../../../../shared/DataNotFound/DataNotFound";
import moment from "moment";
import SlotStatus from "../../../../shared/SlotStatus/SlotStatus";
import { handleDate } from "../../../../shared/CommonFunctions/CommonFunction";


const CandidateInterviewTrackerListView = ({
  pathdata,
}: {
  pathdata?: any;
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [searchKey, setSearchKey] = useState<String>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortOrder, setSortOrder] = useState<any>("asc");
  const [sortBy, setSortBy] = useState("position_name");
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState<number>(0);
  const [interviewerData, setInterviewerData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [selectedScheduleStatus, setSelectedScheduleStatus] = useState<any>();

  const handlePageChange = (event: any, newPage: any) => {
    setPage(newPage);
    setOffset(newPage * rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property: any) => {
    const isAsc = sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(sortOrder === "asc" ? `-${property}` : property);
    setSearchKey(property);
  };

  const handleScheduleStatusFilterChange = (event: any, newValue: any) => {
    setOffset(0);
    setPage(0);
    setSelectedScheduleStatus(newValue);
  };

  useEffect(() => {
    const getInterviewerData = async () => {
      setLoading(true);
      try {
        const response = await API.post("/positions/all/applied", {
          offset: offset,
          limit: rowsPerPage,
          sort: sortBy,
          search: searchValue || "",
          userType:'candidate',
          app_display_status: selectedScheduleStatus?.id ||''
        });
        setInterviewerData(response?.data?.data);
        setTotalRecords(response?.data?.meta?.total_count);
      } catch (error) {
        // Failed to fetch interview tracker listing
      } finally {
        setLoading(false);
      }
    };
    getInterviewerData();
  }, [
    offset,
    searchValue,
    searchKey,
    rowsPerPage,
    sortOrder,
    sortBy,
    selectedScheduleStatus,
  ]);

  return (
    <>
      <Grid2
        container
        sx={{
          marginTop: "2rem",
        }}
      >
        <Grid2 size={{ xs: 12, sm: 4, md: 3 }} className="search-wrap">
          <Search label="Search" onSearch={(val) => setSearchValue(val)} />
        </Grid2>
        <Grid2
          size={{ xs: 12, sm: 3, md: 3 }}
          offset={{ xs: "auto", sm: "auto", md: "auto" }}
          sx={{ marginTop: { xs: "10px", sm: "0px", md: "5px" } }}
          className="schedule-status"
        >
          <Box
            display="flex"
            gap={2}
            sx={{ justifyContent: { xs: "flex-start", sm: "end" } }}
          >
            {/*Schedule Status*/}
            <Autocomplete
              size="small"
              disablePortal
              options={SCHEDULE_STATUS || []}
              getOptionLabel={(option) => option?.title || ""}
              value={selectedScheduleStatus} // Set selected status
              onChange={handleScheduleStatusFilterChange} // Update state on change
              componentsProps={{
                clearIndicator: { disableRipple: true },
                popupIndicator: { disableRipple: true },
              }}
              sx={{
                width: { xs: "50%", sm: "150px", md: "170px" },
                "& .MuiAutocomplete-inputRoot": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                },
                "&:hover:": {
                  border: "transparent",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Schedule Status"
                  variant="outlined"
                  placeholder="Schedule Status"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      backgroundColor: "white",
                      padding: "6px 12px",
                      "&:hover": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        </Grid2>
      </Grid2>
      <Box
        marginTop={1}
        sx={{ overflowY: "auto", flex: "1 1 auto", display: "grid" }}
      >
        <TableContainer className="primary__table">
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "position_name"}
                    direction={sortOrder}
                    onClick={() => handleSort("position_name")}
                  >
                    Position Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell width={'300px'}>Description</TableCell>
                <TableCell>Application Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!!loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ height: "450px" }}>
                      <DataLoader />{" "}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : interviewerData?.length > 0 ? (
                interviewerData?.map((interviewerInfo: any, index: any) => {
                  return (
                    <TableRow>
                      <TableCell>
                        {interviewerInfo?.position_name || "NA"}
                      </TableCell>
                      <TableCell align="center">
                        {interviewerInfo?.application_display_status ==
                          "Interview Scheduled" ||
                        interviewerInfo?.application_display_status ==
                          "Applied" ||
                        interviewerInfo?.application_display_status ==
                          "Interview Rescheduled" ||
                        interviewerInfo?.application_display_status ==
                          "Empanelled" ||
                        interviewerInfo?.application_display_status ==
                          "Not-Empanelled" ||
                        interviewerInfo?.application_display_status ==
                          "Interview Re-scheduled" ||
                        interviewerInfo?.application_display_status ==
                          "Rescheduling in Progress" ||
                        interviewerInfo?.application_display_status ==
                          "Interview Completed" ? (
                          <SlotStatus
                            type={
                              interviewerInfo?.application_display_status ==
                              "Applied"
                                ? "applied"
                                : interviewerInfo?.application_display_status ==
                                  "Interview Scheduled"
                                ? "interviewscheduled"
                                : interviewerInfo?.application_display_status ==
                                    "Interview Rescheduled" ||
                                  interviewerInfo?.application_display_status ==
                                    "Interview Re-scheduled"
                                ? "interviewrescheduled"
                                : interviewerInfo?.application_display_status ==
                                  "Rescheduling in Progress"
                                ? "reschedulinginprogress"
                                : interviewerInfo?.application_display_status ==
                                  "Empanelled"
                                ? "empaneled"
                                : interviewerInfo?.application_display_status ==
                                  "Interview Completed"
                                ? "interviewcompleted"
                                : "notempaneled"
                            }
                          ></SlotStatus>
                        ) : (
                          <>
                            {interviewerInfo?.application_display_status ||
                              "NA"}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ textAlign: "left" }}>
                          {interviewerInfo?.additional_notes || "NA"}
                        </Box>
                      </TableCell>
                      <TableCell>{
                       handleDate(interviewerInfo?.application_date)
                        }</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip title="View" arrow>
                            <IconButton
                              size="small"
                              sx={{ fontSize: "14px", color: "#4880FF" }}
                              className="table__icons"
                              onClick={() =>
                                navigate(
                                  `/admin/candidate-interview-tracker/view/${interviewerInfo?.id}`,
                                  {
                                    state: interviewerInfo,
                                  }
                                )
                              }
                            >
                              <img src={ViewIcons} className="view" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box
                      sx={{
                        height: "450px",
                      }}
                    >
                      <DataNotFound />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {interviewerData?.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>
    </>
  );
};
export default CandidateInterviewTrackerListView;
